import React, { useState, useEffect, useMemo } from "react";
import Dashboardsidebar from "../../layouts/Dashboardsidebar";
import { useLocation, useNavigate } from "react-router-dom";
import { RoutesPath } from "../../constants";
import PropTypes from "prop-types";
import { injectModels } from "../../redux/injectModels";
import SuccessModal from "../../layouts/SuccessModal";
import Footer from "../../layouts/Footer";



const ManageScreen = ({ auth, loader }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("draft");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);

  /* PDF modal states */

  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);


  const [tabCounts, setTabCounts] = useState({
    draft: 0,
    in_progress: 0,
    completed: 0,
    cancelled: 0,
  });

  const rowsPerPage = 5;

  const tabs = [
    { key: "draft", label: "Draft" },
    { key: "in_progress", label: "In Progress" },
    { key: "completed", label: "Completed" },
    { key: "cancelled", label: "Cancelled" },
  ];

  const fetchTabCounts = async () => {
    const statuses = ["draft", "in_progress", "completed", "cancelled"];
    let counts = {};

    for (const status of statuses) {
      try {
        const res = await auth.getAgreementsDetails({ status, page: 1, limit: 1 });
        counts[status] = res?.data?.total || 0;
      } catch {
        counts[status] = 0;
      }
    }
    setTabCounts(counts);
  };

  const fetchAgreements = async (status, page, limit) => {
    try {
      loader.setLoading(true);
      const res = await auth.getAgreementsDetails({ status, page, limit });

      if (res?.success || res?.status === 200) {
        const api = res.data;
        setData(api.agreements || []);
        setTotalRecords(api.total || 0);
        setTotalPages(Math.ceil((api.total || 0) / limit));
        setCurrentPage(api.page || page);

        setTabCounts((prev) => ({ ...prev, [status]: api.total || 0 }));
      }
    } finally {
      loader.setLoading(false);
    }
  };

  useEffect(() => {
    const tabFromNav = location.state?.activeTab || "draft";
    setActiveTab(tabFromNav);
    fetchTabCounts();
    fetchAgreements(tabFromNav, 1, rowsPerPage);
    window.history.replaceState({}, document.title);
  }, [location.state]);

  useEffect(() => {
    fetchAgreements(activeTab, currentPage, rowsPerPage);
  }, [activeTab, currentPage]);

  const sortedData = useMemo(() => {
    let sorted = [...data];
    if (sortConfig.key) {
      sorted.sort((a, b) =>
        a[sortConfig.key] < b[sortConfig.key]
          ? sortConfig.direction === "asc" ? -1 : 1
          : sortConfig.direction === "asc" ? 1 : -1
      );
    }
    return sorted;
  }, [data, sortConfig]);

  const requestSort = (key) => {
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  const getSortArrow = (key) =>
    sortConfig.key !== key ? "↕" : sortConfig.direction === "asc" ? "↑" : "↓";

  const handleOpenSend = async (row) => {
    loader.setLoading(true);
    const res = await auth.getEditAgreementDetails(row.id);
    loader.setLoading(false);

    if (res?.success) {
      navigate(RoutesPath.SENDPAGE, {
        state: { agreementData: res.data, mode: "view" },
      });
    }
  };

  const handleEdit = async (row) => {
    loader.setLoading(true);
    const res = await auth.getEditAgreementDetails(row.id);
    loader.setLoading(false);

    if (res?.success) {
      navigate(RoutesPath.SENDPAGE, {
        state: { agreementData: res.data, mode: "edit" },
      });
    }
  };

  // const handleDownload = async (row) => {
  //   loader.setLoading(true);
  //   const url = await auth.downloadAgreementFile(row.id);
  //   loader.setLoading(false);

  //   if (!url) return alert("File not found");
  //   window.open(url, "_blank");
  // };

  /* OPEN PREVIEW MODAL */
  const handlePreview = async (row) => {
    console.log("========== handlePreview CLICK ==========");
    console.log("row:", row);

    try {
      loader.setLoading(true);

      console.log("Calling auth.previewAgreement...");
      const res = await auth.previewAgreement(row.id);
      console.log("previewAgreement RESULT:", res);

      loader.setLoading(false);

      const previewUrl =
        res?.data?.documents?.[0]?.preview_url;

      console.log("Extracted previewUrl:", previewUrl);

      if (!res?.success || !previewUrl) {
        console.warn("Preview NOT available condition hit");
        setMessage(res?.message || "Preview not available for this document.");
        setPreviewUrl(null);
        setSuccess(true);
        return;
      }

      console.log("Preview AVAILABLE, showing success modal");

      setMessage("Opening document preview...");
      setPreviewUrl(previewUrl);
      setSuccess(true);

    } catch (err) {
      loader.setLoading(false);
      console.error("handlePreview CATCH:", err);
      setMessage("Unexpected error while opening preview.");
      setSuccess(true);
    }
  };

  return (
    <Dashboardsidebar>
      <div className="manage-card">

        {/* Tabs */}
        <div className="tab-header">
          {tabs.map((t) => (
            <button
              key={t.key}
              className={activeTab === t.key ? "tab active" : "tab"}
              onClick={() => {
                setActiveTab(t.key);
                setCurrentPage(1);
              }}
            >
              {t.label} ({tabCounts[t.key]})
            </button>
          ))}
        </div>

        {/* Table */}
        <table className="manage-table">
          <thead>
            <tr>
              <th onClick={() => requestSort("recipient_name")}>
                Recipient {getSortArrow("recipient_name")}
              </th>
              <th onClick={() => requestSort("agreement_name")}>
                Title {getSortArrow("agreement_name")}
              </th>
              <th>Actions</th>
              <th onClick={() => requestSort("due_date")}>
                Due Date {getSortArrow("due_date")}
              </th>
            </tr>
          </thead>

          <tbody>
            {sortedData.map((row) => (
              <tr key={row.id}>
                <td>
                  <div className="recipient-name">
                    {row.recipient_name ||
                      row.recipient_email ||
                      row.recipients?.[0]?.name ||
                      "—"}
                  </div>

                  {/* Draft & In Progress → progress text */}
                  {(activeTab === "draft" || activeTab === "in_progress") && (
                    <div className="recipient-subtext">
                      {row.completion_summary || "—"}
                    </div>
                  )}

                  {/* Completed → company / org name */}
                  {activeTab === "completed" && (
                    <div className="recipient-subtext">
                      {row.company_name ||
                        row.organization_name ||
                        row.org_name ||
                        row.completion_summary ||  // fallback safety
                        "—"}
                    </div>
                  )}

                  {/* Cancelled → completion_summary contains company name */}
                  {activeTab === "cancelled" && (
                    <div className="recipient-subtext">
                      {row.completion_summary || "—"}
                    </div>
                  )}
                </td>
                <td>{row.file_name || row.agreement_name || "—"}</td>

                <td className="actions">
                  {activeTab === "draft" && (
                    <button className="icon-btn" onClick={() => handleEdit(row)}>
                      <i className="fa-solid fa-pen" />
                    </button>
                  )}

                  {activeTab === "in_progress" && (
                    <button className="btn btn-open" onClick={() => handleOpenSend(row)}>Open</button>
                  )}

                  {(activeTab === "completed" || activeTab === "cancelled") && (
                    <>
                      <button className="btn btn-open" onClick={() => handlePreview(row)}>Open</button>
                      {/* <button className="icon-btn" onClick={() => handleDownload(row)}>
                        <i className="fa-solid fa-download" />
                      </button> */}
                    </>
                  )}
                </td>

                <td>{row.due_date?.substring(0, 10) || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination-wrapper">
        <div className="pagination-info">
          Page {currentPage} of {totalPages} — Total: {totalRecords}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              &lt;&lt;
            </button>

            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index}
                className={`page-btn ${currentPage === index + 1 ? "active" : ""}`}
                onClick={() => setCurrentPage(index + 1)}
              >
                {index + 1}
              </button>
            ))}

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              &gt;&gt;
            </button>
          </div>
        )}
      </div>
      {success && (
        <SuccessModal
          show={success}
          message={message}
          onClose={() => {
            setSuccess(false);

            if (previewUrl) {
              window.open(previewUrl, "_blank", "noopener,noreferrer");
              setPreviewUrl(null);
            }
          }}
        />
      )}
      <Footer variant="dashboard" />
    </Dashboardsidebar>
  );
};

ManageScreen.propTypes = {
  auth: PropTypes.object.isRequired,
  loader: PropTypes.object.isRequired,
};

export default injectModels(["auth", "loader"])(ManageScreen);